'use client';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import type { PlacedComponent, Wire, WireColor, ComponentType } from './circuitTypes';
import { COMPONENT_DEFINITIONS, WIRE_COLOR_MAP } from './circuitTypes';
import type { CircuitResult } from './circuitTypes';
import CanvasComponent from './CanvasComponent';

interface WireInProgress {
  fromComponentId: string;
  fromTerminalId: string;
  fromX: number;
  fromY: number;
  color: WireColor;
  mouseX: number;
  mouseY: number;
}

interface Props {
  components: PlacedComponent[];
  wires: Wire[];
  selectedColor: WireColor;
  simulationMode: boolean;
  selectedComponentId: string | null;
  onSelectComponent: (id: string | null) => void;
  onDropComponent: (type: ComponentType, x: number, y: number) => void;
  onMoveComponent: (id: string, x: number, y: number) => void;
  onDeleteComponent: (id: string) => void;
  onAddWire: (wire: Wire) => void;
  onDeleteWire: (wireId: string) => void;
  onToggleSwitch: (id: string) => void;
  circuitResult: CircuitResult | null;
}

let wireCounter = 0;

function snapToGrid(v: number, grid = 20) {
  return Math.round(v / grid) * grid;
}

export default function SimulatorCanvas({
  components, wires, selectedColor, simulationMode,
  selectedComponentId, onSelectComponent,
  onDropComponent, onMoveComponent, onDeleteComponent,
  onAddWire, onDeleteWire, onToggleSwitch, circuitResult,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [wireInProgress, setWireInProgress] = useState<WireInProgress | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragOver, setIsDragOver] = useState(false);

  const getSVGPoint = useCallback((e: React.MouseEvent | MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const getTerminalAbsPos = useCallback(
    (comp: PlacedComponent, terminalIdx: number) => {
      const def = COMPONENT_DEFINITIONS[comp.type];
      const t = def.terminals[terminalIdx];
      if (!t) return { x: comp.x, y: comp.y };
      return { x: comp.x + t.relX, y: comp.y + t.relY };
    },
    []
  );

  const handleTerminalClick = useCallback(
    (compId: string, terminalIdx: number, absX: number, absY: number) => {
      if (!wireInProgress) {
        const terminalId = `${compId}-t${terminalIdx}`;
        setWireInProgress({
          fromComponentId: compId,
          fromTerminalId: terminalId,
          fromX: absX,
          fromY: absY,
          color: selectedColor,
          mouseX: absX,
          mouseY: absY,
        });
      } else {
        const terminalId = `${compId}-t${terminalIdx}`;
        if (wireInProgress.fromComponentId === compId && wireInProgress.fromTerminalId === terminalId) {
          setWireInProgress(null);
          return;
        }
        wireCounter += 1;
        onAddWire({
          id: `wire-${wireCounter}`,
          fromComponentId: wireInProgress.fromComponentId,
          fromTerminalId: wireInProgress.fromTerminalId,
          toComponentId: compId,
          toTerminalId: terminalId,
          color: wireInProgress.color,
        });
        setWireInProgress(null);
      }
    },
    [wireInProgress, selectedColor, onAddWire]
  );

  const handleSVGMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (wireInProgress) {
        const pt = getSVGPoint(e);
        setWireInProgress((prev) => prev ? { ...prev, mouseX: pt.x, mouseY: pt.y } : null);
      }
      if (draggingId) {
        const pt = getSVGPoint(e);
        onMoveComponent(
          draggingId,
          snapToGrid(pt.x - dragOffset.x),
          snapToGrid(pt.y - dragOffset.y)
        );
      }
    },
    [wireInProgress, draggingId, dragOffset, getSVGPoint, onMoveComponent]
  );

  const handleSVGMouseUp = useCallback(() => {
    setDraggingId(null);
  }, []);

  const handleSVGClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (e.target === svgRef.current || (e.target as SVGElement).classList.contains('canvas-bg')) {
        onSelectComponent(null);
        if (wireInProgress) setWireInProgress(null);
      }
    },
    [onSelectComponent, wireInProgress]
  );

  const handleComponentMouseDown = useCallback(
    (e: React.MouseEvent, comp: PlacedComponent) => {
      e.stopPropagation();
      if (wireInProgress) return;
      onSelectComponent(comp.instanceId);
      const pt = getSVGPoint(e);
      setDraggingId(comp.instanceId);
      setDragOffset({ x: pt.x - comp.x, y: pt.y - comp.y });
    },
    [wireInProgress, onSelectComponent, getSVGPoint]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<SVGSVGElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const type = e.dataTransfer.getData('componentType') as ComponentType;
      if (!type) return;
      const pt = getSVGPoint(e as unknown as React.MouseEvent);
      onDropComponent(type, snapToGrid(pt.x - 35), snapToGrid(pt.y - 35));
    },
    [getSVGPoint, onDropComponent]
  );

  // Determine which lamps are lit in simulation mode
  const litLamps = new Set<string>();
  if (simulationMode && circuitResult?.canSimulate) {
    // All switches in the circuit must be ON for lamps to light
    const allSwitches = components.filter(
      (c) => c.type === 'switch-one-way' || c.type === 'switch-two-way'
    );
    const allSwitchesOn = allSwitches.length === 0 || allSwitches.every((sw) => sw.switchState === true);

    if (allSwitchesOn) {
      for (const comp of components) {
        if (comp.type === 'lamp') {
          litLamps.add(comp.instanceId);
        }
      }
    }
  }

  return (
    <div className="flex-1 relative overflow-hidden bg-slate-50">
      {/* Canvas instructions */}
      {components.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="text-center">
            <div className="text-5xl mb-4">🔌</div>
            <p className="text-base font-semibold text-slate-400 mb-1">Canvas is empty</p>
            <p className="text-sm text-slate-300">Drag components from the left sidebar onto this canvas</p>
          </div>
        </div>
      )}

      <svg
        ref={svgRef}
        className={`w-full h-full canvas-bg select-none ${isDragOver ? 'bg-blue-50' : ''}`}
        style={{ cursor: wireInProgress ? 'crosshair' : draggingId ? 'grabbing' : 'default' }}
        onMouseMove={handleSVGMouseMove}
        onMouseUp={handleSVGMouseUp}
        onClick={handleSVGClick}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
      >
        {/* Grid */}
        <defs>
          <pattern id="canvas-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e2e8f0" strokeWidth="0.5"/>
          </pattern>
          <pattern id="canvas-grid-major" width="100" height="100" patternUnits="userSpaceOnUse">
            <rect width="100" height="100" fill="url(#canvas-grid)" />
            <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#cbd5e1" strokeWidth="0.8"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#canvas-grid-major)" className="canvas-bg" />

        {/* Rendered wires */}
        {wires.map((wire) => {
          const fromComp = components.find((c) => c.instanceId === wire.fromComponentId);
          const toComp = components.find((c) => c.instanceId === wire.toComponentId);
          if (!fromComp || !toComp) return null;
          const fromDef = COMPONENT_DEFINITIONS[fromComp.type];
          const toDefComp = COMPONENT_DEFINITIONS[toComp.type];
          const fromIdx = parseInt(wire.fromTerminalId.split('-t')[1], 10);
          const toIdx = parseInt(wire.toTerminalId.split('-t')[1], 10);
          const fromT = fromDef.terminals[fromIdx];
          const toT = toDefComp.terminals[toIdx];
          if (!fromT || !toT) return null;
          const x1 = fromComp.x + fromT.relX;
          const y1 = fromComp.y + fromT.relY;
          const x2 = toComp.x + toT.relX;
          const y2 = toComp.y + toT.relY;
          const midX = (x1 + x2) / 2;
          const strokeColor = WIRE_COLOR_MAP[wire.color].hex;
          return (
            <g key={wire.id} className="group">
              <path
                d={`M ${x1} ${y1} C ${midX} ${y1} ${midX} ${y2} ${x2} ${y2}`}
                stroke={strokeColor}
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                className="transition-all duration-150"
              />
              {/* Invisible wider hit area for deletion */}
              <path
                d={`M ${x1} ${y1} C ${midX} ${y1} ${midX} ${y2} ${x2} ${y2}`}
                stroke="transparent"
                strokeWidth="12"
                fill="none"
                className="cursor-pointer"
                onClick={(e) => { e.stopPropagation(); onDeleteWire(wire.id); }}
                title="Click to delete wire"
              />
            </g>
          );
        })}

        {/* Wire in progress */}
        {wireInProgress && (
          <path
            d={`M ${wireInProgress.fromX} ${wireInProgress.fromY} L ${wireInProgress.mouseX} ${wireInProgress.mouseY}`}
            stroke={WIRE_COLOR_MAP[wireInProgress.color].hex}
            strokeWidth="2.5"
            fill="none"
            strokeDasharray="6 4"
            strokeLinecap="round"
            className="pointer-events-none"
          />
        )}

        {/* Components */}
        {components.map((comp) => (
          <CanvasComponent
            key={comp.instanceId}
            component={comp}
            isSelected={selectedComponentId === comp.instanceId}
            isLit={litLamps.has(comp.instanceId)}
            simulationMode={simulationMode}
            wireInProgress={wireInProgress !== null}
            onMouseDown={(e) => handleComponentMouseDown(e, comp)}
            onTerminalClick={handleTerminalClick}
            onDelete={() => onDeleteComponent(comp.instanceId)}
            onToggleSwitch={() => onToggleSwitch(comp.instanceId)}
          />
        ))}
      </svg>
    </div>
  );
}